import type { Option } from '@src/components/combobox/ComboboxBehavior';

/** Which of the five condition sources an option came from. */
export type OptionSource = 'scenario' | 'task' | 'metric' | 'profile' | 'onboarding';

/** One heading-plus-items block inside the condition picker's right-hand panel. */
export interface PanelGroup {
  readonly key: string
  /** Rendered as a heading only when a section shows more than one group. */
  readonly heading?: string
  /**
   * Explicit cmdk group value, surfacing as `data-value` in the DOM. cmdk would
   * otherwise derive it from the heading, and most sections deliberately render
   * no heading now that the sidebar names them - which would leave the e2e
   * specs (and anything else keying off `[data-value="tasks"]`) with nothing to
   * hold on to. Inert for matching: this Command runs with shouldFilter={false}.
   */
  readonly dataValue: string
  readonly source: OptionSource
  readonly items: Option[]
}

/** One entry in the left sidebar, plus the panel content it selects. */
export interface PickerSection {
  readonly id: string
  readonly label: string
  /** Sits under the non-clickable "Scenarios" label in the sidebar. */
  readonly isScenario: boolean
  /**
   * Text the spotlight may match to keep this whole section. Only scenario
   * names qualify - matching the static labels ("Tasks", "All Scenarios")
   * would make a query like "scenario" retain everything.
   */
  readonly searchableLabel?: string
  readonly groups: PanelGroup[]
}

/** The synthetic section id for the default, nothing-selected panel. */
export const EVERYTHING_SECTION_ID = 'everything';

/**
 * LF360-1938: the default panel, shown until the user picks a sidebar entry -
 * every source at once, each under its own heading, so the picker opens on the
 * full list rather than pre-narrowed to one section.
 *
 * "All Scenarios" is skipped because it is itself a composite of the current
 * scenario plus every other one; including it would print those questions
 * twice. Every remaining section contributes each of its groups exactly once,
 * which is also what keeps the group keys unique here.
 *
 * Returns `undefined` rather than an empty section when nothing survives, so
 * the caller renders the "nothing matches" empty state instead of an empty
 * panel.
 *
 * @param visibleSections the sidebar's sections after the spotlight has pruned them.
 */
export const buildEverythingSection = (visibleSections: PickerSection[]): PickerSection | undefined => {
  const groups = visibleSections
    .filter((section) => section.id !== 'all-scenarios')
    .flatMap((section) => section.groups.map((group) => ({
      ...group,
      // Sections carry no heading of their own - the sidebar names them. Here
      // there is no sidebar selection to do that, so the section label has to
      // stand in.
      heading: group.heading ?? section.label,
    })));

  if (!groups.length) return undefined;

  return { id: EVERYTHING_SECTION_ID, label: 'Everything', isScenario: false, groups };
};

/**
 * LF360-1951: which sidebar entry owns an already-selected condition value, so
 * the picker can open on it instead of on everything. A task lands on "Tasks",
 * a question from another scenario on that scenario's own entry.
 *
 * "All Scenarios" is skipped for the same reason buildEverythingSection skips
 * it: it repeats what the per-scenario entries already hold, and it is the
 * broader of the two answers. Sections are searched in build order, so the
 * single-source entries win over it either way.
 *
 * @param sections the sidebar's sections, unpruned - the value's home does not
 *        depend on what the spotlight currently keeps.
 * @param value the criterion's current value, or undefined when nothing is set.
 * @returns the section id to open on, or `null` for "no selection, show everything".
 */
export const findSectionIdForValue = (
  sections: PickerSection[],
  value: string | undefined,
): string | null => {
  if (!value) return null;

  const owner = sections.find((section) => (
    section.id !== 'all-scenarios'
    && section.groups.some((group) => group.items.some((option) => option.value === value))
  ));

  return owner?.id ?? null;
};
